package fr.manyverse.app.presentation.components.modules.combat

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.domain.model.CombatParticipant
import fr.manyverse.app.domain.model.CombatParticipantType
import fr.manyverse.app.presentation.components.CharacterAvatar
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.combat_adversary_label
import manyverse.shared.generated.resources.icon_skull
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

/**
 * Avatar d'un participant au combat :
 * - personnage : son avatar s'il en a défini un, sinon l'icône par défaut ;
 * - adversaire : une tête de mort dans un cercle teinté.
 *
 * Les avatars des personnages sont résolus depuis [characters] (signed URL propre
 * au lecteur courant). Un participant absent de la liste retombe sur l'icône par défaut.
 */
@Composable
fun CombatParticipantAvatar(
    participant: CombatParticipant,
    characters: List<Character>,
    size: Dp = 48.dp,
    modifier: Modifier = Modifier
) {
    when (participant.type) {
        CombatParticipantType.ADVERSARY -> AdversaryAvatar(size, modifier)
        CombatParticipantType.CHARACTER -> {
            val character = characters.firstOrNull {
                it.id == participant.characterId || it.userId == participant.userId
            }
            CharacterAvatar(
                imageUrl = character?.signedImageUrl,
                characterName = participant.name,
                size = size,
                modifier = modifier
            )
        }
    }
}

@Composable
private fun AdversaryAvatar(
    size: Dp,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .size(size)
            .clip(CircleShape)
            .background(MaterialTheme.colorScheme.errorContainer),
        contentAlignment = Alignment.Center
    ) {
        Icon(
            painter = painterResource(Res.drawable.icon_skull),
            contentDescription = stringResource(Res.string.combat_adversary_label),
            modifier = Modifier.size(size * 0.6f),
            tint = MaterialTheme.colorScheme.onErrorContainer
        )
    }
}
